import {Component, OnInit} from '@angular/core';
import {toDataURL} from '@progress/kendo-angular-excel-export';
import {CaixaService} from '../services/caixa.service';
import {ConstantsService} from '../../fidelidade/ConstantsService';

@Component({
  selector: 'app-consolidado-caixa',
  templateUrl: './consolidado-caixa.component.html',
  styleUrls: ['./consolidado-caixa.component.scss']
})
export class ConsolidadoCaixaComponent implements OnInit {
  carregando = false;
  exportando = false;
  erro = '';
  periodoAtivo = 'hoje';
  dataInicial: Date;
  dataFinal: Date;
  operadoresSelecionados: string[] = [];
  formasSelecionadas: number[] = [];
  operadoresDisponiveis: any[] = [];
  formasDisponiveis: any[] = [];
  moduloFiadoAtivo = false;
  consolidado: any = this.crieConsolidadoVazio();
  readonly formatoMoedaExcel = '[$R$ -416]#,##0.00';

  constructor(private caixaService: CaixaService, private constantsService: ConstantsService) {
    this.definaHoje();
    this.constantsService.moduloFiado$.subscribe((ativo: boolean) => {
      this.moduloFiadoAtivo = ativo === true;
    });
  }

  ngOnInit(): void {
    this.carregueConsolidado();
  }

  selecionePeriodo(periodo: string) {
    this.periodoAtivo = periodo;
    const hoje = this.inicioDoDia(new Date());

    if(periodo === 'hoje') {
      this.dataInicial = hoje;
      this.dataFinal = hoje;
    } else if(periodo === 'semana') {
      const inicioSemana = new Date(hoje);
      const deslocamento = inicioSemana.getDay() === 0 ? 6 : inicioSemana.getDay() - 1;
      inicioSemana.setDate(inicioSemana.getDate() - deslocamento);
      this.dataInicial = inicioSemana;
      this.dataFinal = hoje;
    } else if(periodo === 'mes') {
      this.dataInicial = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
      this.dataFinal = hoje;
    }

    if(periodo !== 'personalizado')
      this.carregueConsolidado();
  }

  marquePeriodoPersonalizado() {
    this.periodoAtivo = 'personalizado';
  }

  apliqueFiltros() {
    this.carregueConsolidado();
  }

  limpeFiltros() {
    this.operadoresSelecionados = [];
    this.formasSelecionadas = [];
    this.definaHoje();
    this.carregueConsolidado();
  }

  async exporteExcel() {
    if(this.exportando || this.carregando) return;

    this.exportando = true;
    try {
      const workbook: any = {
        creator: 'Promokit',
        date: new Date(),
        sheets: [
          this.montePlanilha('Por operador', this.consolidado.porOperador || [], 'nome'),
          this.montePlanilha('Por forma', this.consolidado.porFormaPagamento || [], 'descricao')
        ]
      };
      const dataUrl = await toDataURL(workbook);
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = `CONSOLIDADO_CAIXA_${this.formateDataArquivo(this.dataInicial)}_A_${this.formateDataArquivo(this.dataFinal)}.xlsx`;
      link.click();
    } catch(error) {
      this.erro = 'Não foi possível exportar o consolidado.';
    } finally {
      this.exportando = false;
    }
  }

  private carregueConsolidado() {
    this.erro = this.validePeriodo();
    if(this.erro) return;

    this.carregando = true;
    this.caixaService.obtenhaConsolidado({
      dataInicial: this.formateDataApi(this.dataInicial),
      dataFinal: this.formateDataApi(this.dataFinal),
      operadores: this.operadoresSelecionados.join(','),
      formasPagamento: this.formasSelecionadas.join(',')
    }).then((consolidado: any) => {
      this.consolidado = consolidado || this.crieConsolidadoVazio();
      this.operadoresDisponiveis = this.consolidado.filtrosDisponiveis?.operadores || [];
      this.formasDisponiveis = this.consolidado.filtrosDisponiveis?.formasPagamento || [];
      this.carregando = false;
    }).catch((erro: any) => {
      this.erro = typeof erro === 'string' ? erro : (erro?.message || 'Não foi possível carregar o consolidado.');
      this.consolidado = this.crieConsolidadoVazio();
      this.carregando = false;
    });
  }

  private validePeriodo() {
    if(!this.dataInicial || !this.dataFinal)
      return 'Informe as datas inicial e final.';

    const inicio = this.inicioDoDia(this.dataInicial).getTime();
    const fim = this.inicioDoDia(this.dataFinal).getTime();
    if(fim < inicio)
      return 'A data final deve ser maior ou igual à data inicial.';

    const quantidadeDias = Math.floor((fim - inicio) / 86400000) + 1;
    return quantidadeDias > 93 ? 'O período deve ter no máximo 93 dias.' : '';
  }

  private definaHoje() {
    const hoje = this.inicioDoDia(new Date());
    this.periodoAtivo = 'hoje';
    this.dataInicial = hoje;
    this.dataFinal = hoje;
  }

  private inicioDoDia(data: Date) {
    return new Date(data.getFullYear(), data.getMonth(), data.getDate());
  }

  private formateDataApi(data: Date) {
    const pad = (valor: number) => String(valor).padStart(2, '0');
    return `${data.getFullYear()}-${pad(data.getMonth() + 1)}-${pad(data.getDate())}`;
  }

  private formateDataArquivo(data: Date) {
    return this.formateDataApi(data).replace(/-/g, '');
  }

  private formateDataExibicao(data: Date) {
    const pad = (valor: number) => String(valor).padStart(2, '0');
    return `${pad(data.getDate())}/${pad(data.getMonth() + 1)}/${data.getFullYear()}`;
  }

  private montePlanilha(nome: string, dados: any[], campoDescricao: string) {
    const totais = this.consolidado.totais || this.crieTotaisVazios();
    const linhas: any[] = [
      {cells: [{value: 'Consolidado de Caixa', bold: true, fontSize: 16}]},
      {cells: [{value: `Período de abertura: ${this.formateDataExibicao(this.dataInicial)} a ${this.formateDataExibicao(this.dataFinal)}`}]},
      {cells: [{value: `Operadores: ${this.descrevaOperadoresSelecionados()}`}]},
      {cells: [{value: `Formas de pagamento: ${this.descrevaFormasSelecionadas()}`}]},
      {cells: [
        {value: 'Total de abertura dos caixas:', bold: true},
        {value: Number(totais.saldoInicial || 0), format: this.formatoMoedaExcel, bold: true}
      ]},
      {cells: []},
      {cells: [
        {value: nome, bold: true},
        {value: 'Movimentações', bold: true},
        {value: 'Entradas', bold: true},
        {value: 'Estornos/Saídas', bold: true},
        {value: 'Sangrias', bold: true},
        ...(this.moduloFiadoAtivo ? [{value: 'Fiado', bold: true}] : []),
        {value: 'Resultado', bold: true}
      ]},
      {cells: this.monteCelulasValores('TOTAL', totais, true)}
    ];

    dados.forEach((item: any) => linhas.push({cells: this.monteCelulasValores(item[campoDescricao], item)}));

    return {
      name: nome,
      columns: [
        {width: 220}, {width: 110}, {width: 120}, {width: 130}, {width: 120},
        ...(this.moduloFiadoAtivo ? [{width: 120}] : []),
        {width: 120}
      ],
      rows: linhas,
      freezePane: {rowSplit: 7}
    };
  }

  private monteCelulasValores(descricao: string, valores: any, negrito = false) {
    const moeda = (valor: number) => ({value: Number(valor || 0), format: this.formatoMoedaExcel, bold: negrito});
    return [
      {value: descricao || '-', bold: negrito},
      {value: Number(valores.quantidadeMovimentacoes || 0), bold: negrito},
      moeda(valores.entradas),
      moeda(valores.estornos),
      moeda(valores.sangrias),
      ...(this.moduloFiadoAtivo ? [moeda(valores.fiado)] : []),
      moeda(valores.resultado)
    ];
  }

  private descrevaOperadoresSelecionados() {
    if(!this.operadoresSelecionados.length) return 'Todos';
    return this.operadoresSelecionados.map((chave: string) => {
      return this.operadoresDisponiveis.find((operador: any) => operador.chave === chave)?.nome || chave;
    }).join(', ');
  }

  private descrevaFormasSelecionadas() {
    if(!this.formasSelecionadas.length) return 'Todas';
    return this.formasSelecionadas.map((id: number) => {
      return this.formasDisponiveis.find((forma: any) => Number(forma.id) === Number(id))?.descricao || `Forma #${id}`;
    }).join(', ');
  }

  private crieTotaisVazios() {
    return {saldoInicial: 0, entradas: 0, estornos: 0, sangrias: 0, fiado: 0, resultado: 0, quantidadeMovimentacoes: 0};
  }

  private crieConsolidadoVazio() {
    return {
      filtrosDisponiveis: {operadores: [], formasPagamento: []},
      totais: this.crieTotaisVazios(),
      porOperador: [],
      porFormaPagamento: []
    };
  }
}
